import type { ReactNode } from "react";
import React from "react";
import Link from "next/link";
import { cn } from "@rosette/ts/src/util/cn.js";
import Image from "next/image";
import { getRoleIcon } from "@/components/icon/getRoleIcon.js";
import { Role } from "@rosette/ts/src/business/role/Role.js";
import rosetteIcon from "@rosette/assets/src/icons/rosette.png";
import { InlineIcon } from "@rosette/web_components/src/ui/InlineIcon.js";
import PremiumIcon from "@/components/icon/PremiumIcon.js";
import type { APIUser } from "@rosette/ts/src/business/api/api_schema.js";


const menuLinkClasses = {
    default: "bg-surface text-strong hover:bg-surface-sunken hover:text-strong",
    logout: "bg-surface text-body hover:bg-surface-sunken",
    premium: cn(
        "bg-linear-to-b/srgb from-accent-light from-[-100%] to-accent text-on-accent",
        "hover:bg-accent hover:bg-none hover:text-accent-soft",
    ),
};


interface AccountMenuLinkProps {
    href: string;
    kind?: keyof typeof menuLinkClasses;
    children: ReactNode;
}


function AccountMenuLink({ href, kind, children }: AccountMenuLinkProps) {
    return (
        <Link
            className={cn(
                "w-full py-4 pr-6 pl-4 text-xl font-bold hover:no-underline",
                menuLinkClasses[kind ?? "default"],
            )}
            href={href}>

            {children}
        </Link>
    );
}


interface AccountMenuProps {
    className?: string;
    user: APIUser;
}


export function AccountMenu({ className, user }: AccountMenuProps) {
    return (
        <div className={cn("flex w-full flex-col items-start bg-surface-sunken text-2xl", className ?? null)}>
            <AccountMenuLink href={`/u/${user.username}`}>
                <InlineIcon size="lg" className="mr-2">
                    <Image
                        src={rosetteIcon}
                        alt="The RoyalUr.net rosette icon, in blue and red" />
                </InlineIcon>

                <span className="align-middle">
                    Profile
                </span>
            </AccountMenuLink>

            <AccountMenuLink href="/premium" kind="premium">
                <InlineIcon size="lg" className="mr-2">
                    <PremiumIcon className="[transform:scale(1.25)_translateX(-0.1em)]" />
                </InlineIcon>
                <span className="align-middle">
                    Premium
                </span>
            </AccountMenuLink>

            <AccountMenuLink href="/settings">
                <InlineIcon size="lg" className="mr-2">
                    <Image
                        src={getRoleIcon(Role.STANDARD)}
                        alt={Role.STANDARD.getDesc()} />
                </InlineIcon>

                <span className="align-middle">
                    Settings
                </span>
            </AccountMenuLink>

            {user.hasRoleAdmin && (
                <AccountMenuLink href="/admin">
                    <InlineIcon size="lg" className="mr-2">
                        <Image
                            src={getRoleIcon(Role.ADMIN)}
                            alt={Role.ADMIN.getDesc()} />
                    </InlineIcon>

                    <span className="align-middle">
                        Admin
                    </span>
                </AccountMenuLink>
            )}

            <AccountMenuLink href="/logout" kind="logout">
                Sign out
            </AccountMenuLink>
        </div>
    );
}
